import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { authApi } from "../services/auth";
import {
  openStripeUrl,
  stripeClientFields,
  stashAppStripeNonce,
  onStripeBrowserClosed,
  wasStripeReturnJustHandled,
} from "../lib/stripeFlow";
import StripeWaitScreen from "../components/StripeWaitScreen";
// Reuse the Stripe-callback visual language (dark surface, friendly cards) so the
// resume flow feels like part of the same Stripe hand-off, not a separate page.
import "./StripeCallbackPage.css";
import { t } from "../i18n";

// Destination for the EMAIL 1 "Complete My Profile" link. The link carries a
// 14-day, multi-use token identifying the talent (no session needed). We exchange
// it for a fresh Stripe onboarding link and send them STRAIGHT into Stripe —
// "Redirecting to Stripe…", no sign-in. See server routes/auth.js #resume-onboarding.
//
// There is deliberately NO dead-end "something went wrong" state (per CLAUDE.md
// "cover EVERY path, prefer silent recovery"): an expired/invalid token, or any
// hiccup, quietly falls back to a normal sign-in, where the existing
// needsStripeFinish flag still routes them to /finish-stripe.
type Status = "loading" | "redirecting" | "rejected";

const StripeResumePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<Status>("loading");
  const [rejectionReason, setRejectionReason] = useState<string>("");
  const navigate = useNavigate();
  // Sheet-close recovery (build 41): on native the Stripe sheet is presented
  // OVER this page, so closing it leaves THIS page on screen. Without a
  // watcher it stayed on "Redirecting to Stripe…" forever — the black-screen
  // report. A close means they are done in there (or bailed): sign-in is the
  // one forward path from here, and needsStripeFinish routes them on from it.
  const sheetWatchRef = useRef<null | (() => void)>(null);
  useEffect(() => () => sheetWatchRef.current?.(), []);

  useEffect(() => {
    const token = searchParams.get("token");
    let cancelled = false;

    // No token at all — nothing to resume; send them to sign in.
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    (async () => {
      try {
        await authApi.csrfToken();
      } catch {}

      try {
        // Inside the app's in-app browser sheet IS_NATIVE is false, but the
        // app tag rides the URL (the server appends app=1 to its refresh
        // loop) — honor it so an expired link mid-loop can never silently
        // downgrade the round trip to web-flavored links.
        const fromApp = searchParams.get("app") === "1";
        const data = await authApi.resumeOnboarding({
          token,
          ...stripeClientFields(),
          ...(fromApp ? { client: "app" } : {}),
        });
        if (cancelled) return;
        // App-held copy of the fresh completion key (no-op in a browser
        // context — the hand-off card is the path there).
        if (data?.appNonce) stashAppStripeNonce(data.appNonce);

        // Still needs Stripe — we minted a fresh link. Show "Redirecting to
        // Stripe…" and hand them straight off (in-app sheet on native).
        if (data?.stripeUrl) {
          setStatus("redirecting");
          const stop = onStripeBrowserClosed(() => {
            stop();
            sheetWatchRef.current = null;
            if (wasStripeReturnJustHandled()) return;
            navigate("/login", { replace: true });
          });
          sheetWatchRef.current = stop;
          openStripeUrl(data.stripeUrl);
          return;
        }

        // Stripe terminally rejected them — show the contact-support message
        // inline (the /stripe-rejected route is auth-gated, and they have no
        // session here).
        if (data?.rejected) {
          setRejectionReason(data.code || "");
          setStatus("rejected");
          return;
        }

        // done === already onboarded (or silently rescued): the email did its
        // job — send them to sign in and use the app.
        navigate("/login", { replace: true });
      } catch {
        if (cancelled) return;
        // Expired/invalid token or a transient server error — friendliest path
        // forward is a normal sign-in, never a dead end.
        navigate("/login", { replace: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate]);

  if (status === "loading" || status === "redirecting") {
    return (
      <StripeWaitScreen message={status === "redirecting" ? t("stripeResumePage.takingYouToStripe") : t("stripeResumePage.oneMoment")} />
    );
  }

  // Terminal rejection (KYC failure, sanctions, prohibited industry). Re-entering
  // Stripe would bounce to the same rejection, so contact-support is the only path.
  return (
    <div className="stripe-callback-container">
      <div className="stripe-box">
        <h2 className="stripe-incomplete-title">{t("stripeResumePage.weCouldnTVerify")}</h2>
        <p className="stripe-incomplete-message">
          {t("stripeResumePage.stripeWasnTAble")}
        </p>
        <a
          className="stripe-return-button"
          href="mailto:support@requesta.app?subject=Account%20verification%20help"
          style={{ display: "inline-block", textDecoration: "none" }}
        >
          {t("stripeResumePage.contactSupport")}
        </a>
        {rejectionReason && (
          <p style={{ marginTop: 16, fontSize: "0.78rem", color: "rgba(229,231,235,0.45)", fontFamily: "ui-monospace, Menlo, monospace" }}>
            {t("stripeResumePage.ref", { rejectionReason })}
          </p>
        )}
      </div>
    </div>
  );
};

export default StripeResumePage;
